// <s-product-gallery> turns the product media track into a one-at-a-time carousel.
// Prev/next and thumbnail buttons target this element with custom commands
// (--prev, --next, --go with data-index on the source button). The track itself
// is native snap scrolling, so swipe and trackpad scrolling work without JS.
class ProductGallery extends HTMLElement {
  #track = null;
  #frame = 0;
  #onCommand = (event) => this.#handleCommand(event);
  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  connectedCallback() {
    this.#track = this.querySelector("[data-gallery-track]");
    if (!this.#track) return;
    this.addEventListener("command", this.#onCommand);
    this.#track.addEventListener("scroll", this.#onScroll, { passive: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener("command", this.#onCommand);
    this.#track?.removeEventListener("scroll", this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  get #slides() {
    return Array.from(this.#track.children);
  }

  get #index() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #handleCommand(event) {
    const count = this.#slides.length;
    if (!count) return;
    let target = this.#index;
    if (event.command === "--prev") target = Math.max(0, target - 1);
    else if (event.command === "--next") target = Math.min(count - 1, target + 1);
    else if (event.command === "--go") target = Number(event.source?.dataset.index ?? target);
    else return;
    this.#goTo(target);
  }

  #goTo(index) {
    const slide = this.#slides[index];
    if (!slide) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: reduceMotion ? "auto" : "smooth" });
  }

  #sync() {
    const index = this.#index;
    const last = this.#slides.length - 1;
    this.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
      const active = Number(thumb.dataset.index) === index;
      thumb.toggleAttribute("aria-current", active);
      if (active) thumb.setAttribute("aria-current", "true");
    });
    const prev = this.querySelector("[data-gallery-prev]");
    const next = this.querySelector("[data-gallery-next]");
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= last;
  }
}

if (!customElements.get("s-product-gallery")) {
  customElements.define("s-product-gallery", ProductGallery);
}
